import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, ArrowDownward as DownIcon, ArrowUpward as UpIcon, DeleteOutline as RemoveIcon } from '@mui/icons-material';
import { tailoredApi } from '../../services/api';
import { TailoredDocument, TailoredVersion } from '../../types';
import { errorMessage } from '../../utils/errors';

interface Props {
  /** The version to edit, with its `document`; null closes the editor. */
  version: TailoredVersion | null;
  onClose: () => void;
  /** Called with the version as saved, once the PDF has been made again. */
  onSaved: (version: TailoredVersion) => void;
}

const forJob = (version: TailoredVersion): string =>
  [version.job_title, version.company_name].filter(Boolean).join(' at ') || 'a job';

const MAX_LINE = 600;

interface LinesProps {
  /** What the lines are under, for the controls' accessible names. */
  heading: string;
  lines: string[];
  onChange: (lines: string[]) => void;
  disabled: boolean;
}

/** The lines under one job or project: each can be reworded, moved, removed; one can be added. */
const Lines: React.FC<LinesProps> = ({ heading, lines, onChange, disabled }) => {
  const set = (index: number, text: string) => onChange(lines.map((line, at) => (at === index ? text : line)));
  const move = (index: number, by: number) => {
    const next = [...lines];
    const [line] = next.splice(index, 1);
    next.splice(index + by, 0, line);
    onChange(next);
  };
  return (
    <Stack spacing={1}>
      {lines.map((line, index) => (
        <Stack key={index} direction="row" spacing={0.5} alignItems="flex-start">
          <TextField
            value={line}
            onChange={(e) => set(index, e.target.value)}
            multiline
            fullWidth
            size="small"
            disabled={disabled}
            error={line.length > MAX_LINE}
            helperText={line.length > MAX_LINE ? `${line.length} of ${MAX_LINE} characters` : undefined}
            inputProps={{ 'aria-label': `Line ${index + 1} under ${heading}` }}
          />
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" aria-label={`Move line ${index + 1} under ${heading} up`} disabled={disabled || index === 0} onClick={() => move(index, -1)}>
                <UpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" aria-label={`Move line ${index + 1} under ${heading} down`} disabled={disabled || index === lines.length - 1} onClick={() => move(index, 1)}>
                <DownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove this line">
            <span>
              <IconButton size="small" aria-label={`Remove line ${index + 1} under ${heading}`} disabled={disabled} onClick={() => onChange(lines.filter((_, at) => at !== index))}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ))}
      <Box>
        <Button size="small" startIcon={<AddIcon />} disabled={disabled || lines.length >= 20} onClick={() => onChange([...lines, ''])}>
          Add a line
        </Button>
      </Box>
    </Stack>
  );
};

/**
 * Editing a tailored resume by hand: the summary, the lines under each job
 * and project, and the skills. Saving makes the PDF again. The headings
 * (jobs, titles, dates, education) are the resume's own and are not edited here.
 */
const TailoredEditor: React.FC<Props> = ({ version, onClose, onSaved }) => {
  const [draft, setDraft] = useState<TailoredDocument | null>(null);
  const [skills, setSkills] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setSaving(false);
    const document = version?.document ?? null;
    setDraft(document ? { ...document, experience: document.experience.map((role) => ({ ...role, bullets: [...role.bullets] })), projects: document.projects.map((project) => ({ ...project, bullets: [...project.bullets] })) } : null);
    setSkills(document ? document.skills.join('\n') : '');
  }, [version]);

  const tooLong = draft ? [...draft.experience, ...draft.projects].some((entry) => entry.bullets.some((line) => line.length > MAX_LINE)) : false;

  const save = async () => {
    if (!version || !draft) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await tailoredApi.update(version.id, {
        summary: draft.summary,
        experience: draft.experience.map((role) => ({ id: role.id, bullets: role.bullets })),
        projects: draft.projects.map((project) => ({ id: project.id, bullets: project.bullets })),
        skills: skills.split('\n'),
      });
      onSaved(saved);
    } catch (e) {
      setError(errorMessage(e, 'Your changes could not be saved.'));
      setSaving(false);
    }
  };

  return (
    <Dialog open={version !== null} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="tailored-editor-title">
      <DialogTitle id="tailored-editor-title">Edit the resume for {version ? forJob(version) : ''}</DialogTitle>
      <DialogContent dividers>
        {version && !draft && (
          <Alert severity="info">
            {version.status === 'ready' ? 'This resume cannot be edited: the resume it was built from is no longer here.' : 'Only a finished resume can be edited.'}
          </Alert>
        )}
        {draft && (
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Reword a line, move it, remove it or add one. Saving makes the PDF again; if the application is still open in
              another tab, JobFlo attaches the new file when you go back to it. Jobs, titles, dates and education come from
              your resume and are not changed here. What you write is yours: JobFlo does not check it against your resume.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Summary"
              value={draft.summary}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              disabled={saving}
              helperText="Leave empty for a resume without a summary."
            />
            {draft.experience.map((role, at) => {
              const heading = [role.title, role.employer].filter(Boolean).join(', ') || 'this job';
              return (
                <Box key={role.id} component="section" aria-label={heading}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {heading}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {role.dates}
                  </Typography>
                  <Lines
                    heading={heading}
                    lines={role.bullets}
                    disabled={saving}
                    onChange={(bullets) => setDraft({ ...draft, experience: draft.experience.map((item, index) => (index === at ? { ...item, bullets } : item)) })}
                  />
                </Box>
              );
            })}
            {draft.projects.map((project, at) => (
              <Box key={project.id} component="section" aria-label={project.name || 'Project'}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {project.name || 'Project'}
                </Typography>
                <Lines
                  heading={project.name || 'this project'}
                  lines={project.bullets}
                  disabled={saving}
                  onChange={(bullets) => setDraft({ ...draft, projects: draft.projects.map((item, index) => (index === at ? { ...item, bullets } : item)) })}
                />
              </Box>
            ))}
            <TextField
              label="Skills"
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={saving}
              helperText='One per line, in the order they should appear. Groups can be written as "Languages: Python, Go", one group per line.'
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {draft && (
          <Button variant="contained" onClick={save} disabled={saving || tooLong}>
            {saving ? 'Saving...' : 'Save and remake the PDF'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TailoredEditor;
